// frontend/src/pages/ProviderManagement.tsx
// FULLY CLEANED VERSION - Mobile-first, NO bulk operations, streamlined

import React, { useState, useCallback, useMemo } from 'react';
import { 
  ArrowLeft,
  Edit
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  useProvidersWithStats,
  useCreateProvider,
  useUpdateProvider,
  useDeleteProvider,
  useVerifyProvider,
  useToggleProviderStatus
} from '@/hooks/useProviders';
import { ProviderFilters, ProviderFormData, Provider } from '@/lib/api/providerService';

// Import mobile-first components only (NO bulk actions)
import ProviderListMobile from '@/components/Provider/ProviderListMobile';
import ProviderEmptyState from '@/components/Provider/ProviderEmptyState';
import ProviderForm from '@/components/Provider/ProviderForm';
import { LoadingState, ErrorState, Pagination, PageHeader, PageFilters, useFilters, StatsGrid, StatItem, FilterPanel } from '@/components/common/ui';
import type { FilterField } from '@/components/common/ui/PageFilters';
import { Building2, Plus, RefreshCw, Shield, CheckCircle, Users, AlertTriangle, XCircle } from 'lucide-react';

type ViewMode = 'list' | 'form' | 'view';

const ProviderManagement: React.FC = () => {
  // State management - simplified (NO selection state)
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);
  const [viewingProvider, setViewingProvider] = useState<Provider | null>(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Filter state using useFilters hook
  const {
    filters: filterValues,
    updateFilter,
    clearFilters: clearFilterValues,
    hasActiveFilters
  } = useFilters({
    search: '',
    city: '',
    verified: '',
    status: ''
  });

  // Memoize filters
  const filters = useMemo<ProviderFilters>(() => ({
    page,
    limit,
    ...(filterValues.search && { search: filterValues.search }),
    ...(filterValues.city && { city: filterValues.city }),
    ...(filterValues.verified && { verified: filterValues.verified }),
    ...(filterValues.status && { status: filterValues.status })
  }), [page, limit, filterValues.search, filterValues.city, filterValues.verified, filterValues.status]);

  // Hooks - removed bulk operations
  const { 
    data: providersData, 
    isLoading, 
    error, 
    refetch 
  } = useProvidersWithStats(filters);
  
  const createProviderMutation = useCreateProvider();
  const updateProviderMutation = useUpdateProvider();
  const deleteProviderMutation = useDeleteProvider();
  const verifyProviderMutation = useVerifyProvider();
  const toggleStatusMutation = useToggleProviderStatus();

  const providers = providersData?.providers || [];
  const pagination = providersData?.pagination;
  const stats = providersData?.stats;

  // Filter fields configuration
  const filterFields = useMemo<FilterField[]>(() => [
    {
      key: 'search',
      label: 'Search Providers',
      type: 'search',
      placeholder: 'Search providers...',
      width: 'lg',
      clearable: true
    },
    {
      key: 'city',
      label: 'City',
      type: 'search',
      placeholder: 'Filter by city...',
      width: 'md',
      clearable: true
    },
    {
      key: 'verified',
      label: 'Verification',
      type: 'select',
      width: 'md',
      options: [
        { label: 'Verified', value: 'verified' },
        { label: 'Pending', value: 'pending' },
        { label: 'Rejected', value: 'rejected' }
      ]
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      width: 'md',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
        { label: 'Suspended', value: 'suspended' }
      ]
    }
  ], []);

  // Event handlers - streamlined
  const handleCreateNew = useCallback(() => {
    setEditingProvider(null);
    setViewMode('form');
  }, []);

  const handleEdit = useCallback((provider: Provider) => {
    setEditingProvider(provider);
    setViewMode('form');
  }, []);

  const handleView = useCallback((provider: Provider) => {
    setViewingProvider(provider);
    setViewMode('view');
  }, []);

  const handleBackToList = useCallback(() => {
    setViewMode('list');
    setEditingProvider(null);
    setViewingProvider(null);
  }, []);

  const handleRefresh = useCallback(() => {
    refetch();
    toast.success('Provider list refreshed');
  }, [refetch]);

  const handleFormSubmit = useCallback(async (data: ProviderFormData) => {
    try {
      if (editingProvider) {
        await updateProviderMutation.mutateAsync({ 
          id: editingProvider.provider_id, 
          data 
        });
        toast.success('Provider updated successfully');
      } else {
        await createProviderMutation.mutateAsync(data);
        toast.success('Provider created successfully');
      }
      handleBackToList();
    } catch (error: any) {
      toast.error(error.message || 'Operation failed');
    }
  }, [editingProvider, updateProviderMutation, createProviderMutation, handleBackToList]);

  const handleDelete = useCallback(async (providerId: number) => {
    if (!confirm('Are you sure you want to delete this provider?')) return;
    
    try {
      await deleteProviderMutation.mutateAsync(providerId);
      toast.success('Provider deleted successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete provider');
    }
  }, [deleteProviderMutation]);

  const handleVerify = useCallback(async (providerId: number) => {
    try {
      await verifyProviderMutation.mutateAsync(providerId);
      toast.success('Provider verified successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to verify provider');
    }
  }, [verifyProviderMutation]);

  const handleToggleStatus = useCallback(async (providerId: number) => {
    try {
      await toggleStatusMutation.mutateAsync(providerId);
      toast.success('Provider status updated successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update provider status');
    }
  }, [toggleStatusMutation]);

  // Search and filter handlers
  const handleFilterChange = useCallback((key: string, value: any) => {
    updateFilter(key, value);
    setPage(1); // Reset to first page when filtering
  }, [updateFilter]);

  const handleClearFilters = useCallback(() => {
    clearFilterValues();
    setPage(1);
  }, [clearFilterValues]);

  // Form view
  if (viewMode === 'form') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="mb-6">
            <button
              onClick={handleBackToList}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4 min-h-[44px] px-2 -mx-2 rounded-lg touch-target"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Providers
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {editingProvider ? 'Edit Provider' : 'Create New Provider'}
            </h1>
          </div>

          <ProviderForm
            initialData={editingProvider || undefined}
            onSubmit={handleFormSubmit}
            onCancel={handleBackToList}
            isLoading={createProviderMutation.isPending || updateProviderMutation.isPending}
          />
        </div>
      </div>
    );
  }

  // View provider details
  if (viewMode === 'view' && viewingProvider) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="mb-6">
            <button
              onClick={handleBackToList}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4 min-h-[44px] px-2 -mx-2 rounded-lg touch-target"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Providers
            </button>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h1 className="text-2xl font-bold text-gray-900">
                {viewingProvider.business_name || 'Provider Details'}
              </h1>
              <button
                onClick={() => handleEdit(viewingProvider)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm flex items-center justify-center min-h-[44px] touch-target"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </button>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4">
              <div className="space-y-6">
                {/* Basic Info */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Business Name</dt>
                      <dd className="text-sm text-gray-900 mt-1">{viewingProvider.business_name || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Business Type</dt>
                      <dd className="text-sm text-gray-900 mt-1">{viewingProvider.business_type || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Email</dt>
                      <dd className="text-sm text-gray-900 mt-1">{viewingProvider.email || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Phone</dt>
                      <dd className="text-sm text-gray-900 mt-1">{viewingProvider.phone || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">City</dt>
                      <dd className="text-sm text-gray-900 mt-1">{viewingProvider.city || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Rating</dt>
                      <dd className="text-sm text-gray-900 mt-1">
                        {viewingProvider.average_rating ? `${viewingProvider.average_rating}/5` : 'No ratings'}
                      </dd>
                    </div>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Status</h3>
                  <div className="flex flex-wrap gap-2">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                      viewingProvider.is_verified
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {viewingProvider.is_verified ? 'Verified' : 'Pending Verification'}
                    </span>
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                      viewingProvider.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {viewingProvider.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {/* Description */}
                {viewingProvider.description && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">{viewingProvider.description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // List view - mobile-first layout (NO bulk actions)
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Simple Page Title - Just text, no card */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Provider Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage service providers and their profiles
          </p>
        </div>

        {/* Stats as Info Section - No card wrapper */}
        {stats && (
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Overview</h2>
            <StatsGrid
              stats={[
                {
                  label: 'Total Providers',
                  value: stats.total || 0,
                  icon: Users,
                  color: 'gray'
                },
                {
                  label: 'Verified',
                  value: stats.verified || 0,
                  icon: CheckCircle,
                  color: 'green'
                },
                {
                  label: 'Active',
                  value: stats.active || 0,
                  icon: Shield,
                  color: 'blue'
                },
                {
                  label: 'Pending',
                  value: stats.pending || 0,
                  icon: AlertTriangle,
                  color: 'yellow'
                },
                {
                  label: 'Suspended',
                  value: stats.suspended || 0,
                  icon: XCircle,
                  color: 'red'
                }
              ]}
              layout="detailed"
              columns={{ mobile: 2, tablet: 3, desktop: 5 }}
            />
          </div>
        )}

        {/* Filters Section - Outside card */}
        <div className="mb-6">
          <FilterPanel
            fields={filterFields}
            values={filterValues}
            onChange={handleFilterChange}
            onClear={handleClearFilters}
            hasActiveFilters={hasActiveFilters}
            variant="default"
            title="Provider Filters"
            defaultExpanded={hasActiveFilters}
          />
        </div>

        {/* Actions - Below filters, above listing */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Provider
            </button>
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Mobile: No card wrapper for max space */}
        <div className="block xl:hidden">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Providers</h3>
            {providersData && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {pagination?.total || providers?.length || 0} total
              </span>
            )}
          </div>
          {isLoading ? (
            <LoadingState
              variant="skeleton"
              title="Loading providers..."
              message="Please wait while we fetch your providers"
              size="md"
            />
          ) : error ? (
            <ErrorState
              type="server"
              title="Error loading providers"
              message={error.message || 'Failed to load providers'}
              onRetry={handleRefresh}
              size="md"
            />
          ) : providers.length === 0 ? (
            <ProviderEmptyState 
              onCreateNew={handleCreateNew}
              hasFilters={hasActiveFilters}
              onClearFilters={handleClearFilters}
            />
          ) : (
            <ProviderListMobile
              providers={providers}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={(id, businessName) => handleDelete(id)}
              onVerify={handleVerify}
              onToggleStatus={handleToggleStatus}
            />
          )}
        </div>

        {/* Desktop: Card wrapper for clean look */}
        <div className="hidden xl:block bg-white rounded-lg border border-gray-200 shadow-sm">
          {/* Card Header with count */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Providers</h3>
              {providersData && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {pagination?.total || providers?.length || 0} total
                </span>
              )}
            </div>
          </div>

          {/* Providers Listing */}
          <div className="p-6">
            {isLoading ? (
              <LoadingState
                variant="skeleton"
                title="Loading providers..."
                message="Please wait while we fetch your providers"
                size="md"
              />
            ) : error ? (
              <ErrorState
                type="server"
                title="Error loading providers"
                message={error.message || 'Failed to load providers'}
                onRetry={handleRefresh}
                size="md"
              />
            ) : providers.length === 0 ? (
              <ProviderEmptyState 
                onCreateNew={handleCreateNew}
                hasFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
              />
            ) : (
              <ProviderListMobile
                providers={providers}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={(id, businessName) => handleDelete(id)}
                onVerify={handleVerify}
                onToggleStatus={handleToggleStatus}
              />
            )}
          </div>
        </div>

        {/* Pagination */}
        {pagination && (
          <div className="flex justify-center mt-6">
            <Pagination
              currentPage={pagination.current_page || 1}
              totalPages={pagination.total_pages || 1}
              totalItems={pagination.total || 0}
              itemsPerPage={pagination.per_page || 10}
              onPageChange={setPage}
              variant="default"
              size="md"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderManagement;